import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Dialog } from './Dialog'
import { useHubLang } from '../lib/i18n'
import { PHOTO_ROOM } from '../lib/hubCopy'
import { signedPhotoUrl } from '../lib/photos'
import { takenText } from '../lib/gallery'
import { TagReason } from './TagReason'
import { PHOTO_WHAT } from '../lib/hubCopy'
import {
  FACETS,
  addVocabValue,
  answerPhotoFeature,
  photoFeaturesFor,
  setTagNote,
  type PhotoFeatures,
  describePhotos,
  downloadPhoto,
  megabytes,
  orientationOf,
  printInches,
  setPhotoDescription,
  setPhotoFeature,
  signedOriginalUrl,
  photoRoomUses,
  photoLocations,
  placeLine,
  removeGalleryPhoto,
  PHOTO_USES,
  type PhotoUse,
  type RoomLocation,
  type RoomUses,
  type DownloadOutcome,
  type DownloadVariant,
  type Facet,
  type RoomPhoto,
  type VocabValue,
} from '../lib/photoRoom'
import { PHOTO_REVIEW } from '../lib/hubCopy'
import { PlaceOnMap } from './PlaceOnMap'
import { galleryDetails, loadReviewQueue, placeName, type GalleryDetails, type QueueItem } from '../lib/photoReview'
import { PhotoReviewButton } from './PhotoReviewButton'
import { PhotoSiteRow } from './PhotoSiteRow'
import { SearchPick } from './SearchPick'

/**
 * 146. One photo in the photo room, in a modal: the picture, what is in it,
 * where it came from, and the downloads.
 *
 * ── WHAT THE METADATA PANEL IS FOR ──────────────────────────────────────────
 *
 * Answers what a marketing person asks before using a photo: may I (who is in
 * it, and who agreed); how big is it (pixels, print size at 300 dpi, the full
 * size if one was kept); when and where (date taken from the camera, who posted
 * it and their business unit, and — 149, photo room admins only — where it was
 * taken); what is in it (the vocabulary); what it may be used for (148: a
 * download says what it is for, and a use the poster opted out of is not
 * offered); and who has already used it.
 *
 * ── TAGS ARE A CONVERSATION WITH ONECONETEC ─────────────────────────────────
 *
 * A suggested value shows its confidence and a Yes / No. Yes makes it the
 * room's; No is remembered, and OneConetec never suggests it on this photo
 * again. An accepted value can be removed (that is a No too). Search and
 * filters count suggested values until somebody says No.
 */
export function PhotoRoomViewer({
  photos,
  index,
  onIndex,
  onClose,
  vocab,
  me,
  reward,
  score,
  onChange,
  onWithdrawn,
  onVocabAdded,
  onRefresh,
  canLocation = false,
  onPlaced,
}: {
  /** 319: a photo was placed on the map (the Gallery map reloads). */
  onPlaced?: (path: string) => void
  /** 149. photo.location: where it was taken, the reason, the full-size copy. */
  canLocation?: boolean
  photos: RoomPhoto[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
  vocab: VocabValue[]
  me: { personId: string; displayName: string }
  reward: number | null
  score?: { score: number; why: string } | null
  onChange: (p: RoomPhoto) => void
  onWithdrawn: (path: string) => void
  onVocabAdded: (v: VocabValue) => void
  onRefresh: () => Promise<void>
}) {
  const lang = useHubLang()
  const t = PHOTO_ROOM[lang]
  const p = photos[index]
  const [url, setUrl] = useState<string | null>(null)
  const [full, setFull] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null)
  const [editing, setEditing] = useState(false)
  const [en, setEn] = useState('')
  const [es, setEs] = useState('')
  const [adding, setAdding] = useState<Facet | null>(null)
  const [newEn, setNewEn] = useState('')
  const [newEs, setNewEs] = useState('')
  /* 305 (FED-72). A new tag may say how to recognise it (its cue, 302), and a
     no asks why; what colleagues answered, with names and reasons. */
  const [newCue, setNewCue] = useState('')
  const tw = PHOTO_WHAT[lang]
  const [askNo, setAskNo] = useState<{ valueId: string; label: string; confidence: number | null } | null>(null)
  const [crowd, setCrowd] = useState<PhotoFeatures | null>(null)
  useEffect(() => {
    if (!p) return
    let live = true
    setCrowd(null)
    photoFeaturesFor([p.path])
      .then((m) => live && setCrowd(m.get(p.path) ?? null))
      .catch(() => {})
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.path, p?.features])
  /* 148. What this photo may be used for, and the use this download is for. */
  const [uses, setUses] = useState<RoomUses | null>(null)
  const [use, setUse] = useState<PhotoUse | null>(null)
  /* 149. Where it was taken, for photo room admins. */
  const [where, setWhere] = useState<RoomLocation | null | undefined>(undefined)
  /* 304. Removing a Gallery upload: asked once, in place, never a browser confirm. */
  const [confirmRemove, setConfirmRemove] = useState(false)
  /* 152. The place NAME and whether a review is open (document users and
     admins), and — admins only — this photo's review requests. */
  const tr = PHOTO_REVIEW[lang]
  const [details, setDetails] = useState<GalleryDetails | null | undefined>(undefined)
  const [reviews, setReviews] = useState<QueueItem[] | null>(null)
  const [detailsRev, setDetailsRev] = useState(0)

  // A new photo is a new panel: the edit box, the notice and the zoom belong
  // to the photo they were opened on.
  useEffect(() => {
    setFull(false)
    setEditing(false)
    setNotice(null)
    setAdding(null)
    setConfirmRemove(false)
  }, [p?.path])

  useEffect(() => {
    if (!p) return
    let live = true
    setUrl(null)
    const get = full ? signedOriginalUrl(p.path) : signedPhotoUrl(p.path)
    get.then((u) => live && setUrl(u)).catch(() => {})
    return () => {
      live = false
    }
  }, [p?.path, full])

  useEffect(() => {
    if (!p) return
    let live = true
    setUses(null)
    setUse(null)
    setWhere(undefined)
    photoRoomUses([p.path])
      .then((m) => {
        if (!live) return
        const u = m.get(p.path) ?? null
        setUses(u)
        setUse(u ? PHOTO_USES.find((k) => u[k]) ?? null : null)
      })
      .catch(() => {})
    setDetails(undefined)
    setReviews(null)
    galleryDetails([p.path])
      .then((m) => live && setDetails(m.get(p.path) ?? null))
      .catch(() => live && setDetails(null))
    if (canLocation) {
      loadReviewQueue('all', p.path)
        .then((r) => live && setReviews(r.items))
        .catch(() => live && setReviews([]))
    }
    if (canLocation) {
      photoLocations([p.path])
        .then((m) => live && setWhere(m.get(p.path) ?? null))
        .catch(() => live && setWhere(null))
    }
    return () => {
      live = false
    }
  }, [p?.path, canLocation, detailsRev])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1)
      if (e.key === 'ArrowRight' && index < photos.length - 1) onIndex(index + 1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [index, photos.length, onIndex])

  const label = (v: { labelEn: string; labelEs: string }) => (lang === 'es' ? v.labelEs : v.labelEn)
  const on = useMemo(() => new Set(p?.features.map((f) => f.valueId) ?? []), [p])

  if (!p) return null

  const dateOf = (iso: string) =>
    new Date(iso).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
  const takenOf = (local: string) => {
    // The camera's wall clock: shown as written, never converted (its zone is unknown).
    const [d, time] = local.split('T')
    return `${d} ${time?.slice(0, 5) ?? ''}${p.takenOffset ? ` (UTC${p.takenOffset})` : ''}`
  }

  async function act(key: string, fn: () => Promise<void>) {
    setBusy(key)
    setNotice(null)
    try {
      await fn()
    } catch {
      setNotice({ text: t.couldNotSave, tone: 'warn' })
    } finally {
      setBusy(null)
    }
  }

  const yes = (valueId: string) =>
    act('f' + valueId, async () => {
      await setPhotoFeature(p.path, valueId, true)
      const v = vocab.find((x) => x.id === valueId)
      const existing = p.features.find((f) => f.valueId === valueId)
      onChange({
        ...p,
        features: existing
          ? p.features.map((f) => (f.valueId === valueId ? { ...f, state: 'accepted' } : f))
          : v
            ? [...p.features, { valueId, facet: v.facet, key: v.key, labelEn: v.labelEn, labelEs: v.labelEs, state: 'accepted', confidence: null }]
            : p.features,
      })
    })

  /* 305: a no asks why first (TagReason); the answer is the room holder's and decides. */
  const no = (valueId: string) => {
    const f = p.features.find((x) => x.valueId === valueId)
    const v = vocab.find((x) => x.id === valueId)
    setAskNo({ valueId, label: f ? label(f) : v ? label(v) : '', confidence: f?.state === 'suggested' ? (f.confidence ?? 0.5) : null })
  }
  const sendNo = (valueId: string, reason: string | null) =>
    act('f' + valueId, async () => {
      await answerPhotoFeature(p.path, valueId, false, reason)
      setAskNo(null)
      onChange({ ...p, features: p.features.filter((f) => f.valueId !== valueId) })
    })

  const addNew = (facet: Facet) =>
    act('new', async () => {
      const id = await addVocabValue(facet, newEn, newEs)
      const v: VocabValue = { id, tenantId: p.tenantId, facet, key: '', labelEn: newEn.trim(), labelEs: newEs.trim() || newEn.trim(), retired: false }
      onVocabAdded(v)
      /* 305: "explain why" for a new tag IS its recognition note (302's cue). */
      if (newCue.trim()) {
        try {
          await setTagNote(id, newCue.trim())
        } catch {
          /* the tag exists; the note can be written in the Tag list */
        }
      }
      setNewCue('')
      await setPhotoFeature(p.path, id, true)
      onChange({ ...p, features: [...p.features, { valueId: id, facet, key: '', labelEn: v.labelEn, labelEs: v.labelEs, state: 'accepted', confidence: null }] })
      setAdding(null)
      setNewEn('')
      setNewEs('')
    })

  const saveDescription = () =>
    act('desc', async () => {
      await setPhotoDescription(p.path, en, es)
      const empty = !en.trim() && !es.trim()
      onChange({
        ...p,
        descriptionEn: empty ? null : en.trim() || null,
        descriptionEs: empty ? null : es.trim() || null,
        descriptionBy: empty ? null : 'person',
      })
      setEditing(false)
    })

  const askAgain = () =>
    act('ask', async () => {
      setNotice({ text: t.asking, tone: 'ok' })
      await describePhotos([p.path], true)
      await onRefresh()
      setNotice(null)
    })

  async function take(variant: DownloadVariant) {
    if (!use) return
    setBusy('dl' + variant)
    setNotice(null)
    try {
      const r = await downloadPhoto(p, variant, use)
      onChange({
        ...p,
        downloadCount: p.downloadCount + 1,
        // 152: who downloaded is a Gallery admin's; nobody else is shown the list.
        downloads: canLocation
          ? [{ personId: me.personId, name: me.displayName, at: new Date().toISOString(), variant, use }, ...p.downloads].slice(0, 10)
          : p.downloads,
        starsPaid: p.starsPaid || r.outcome === 'awarded',
      })
      setNotice({
        text: r.fileSaved ? outcomeText(t, r.outcome, p.uploaderName, r.stars) : t.notSaved,
        tone: r.fileSaved && r.outcome !== 'failed' ? 'ok' : 'warn',
      })
    } catch {
      setNotice({ text: t.withdrawn, tone: 'warn' })
      onWithdrawn(p.path)
    } finally {
      setBusy(null)
    }
  }

  const description = lang === 'es' ? p.descriptionEs ?? p.descriptionEn : p.descriptionEn ?? p.descriptionEs
  const orient = orientationOf(p.originalWidth ?? p.width, p.originalHeight ?? p.height)
  const print = p.originalWidth && p.originalHeight ? printInches(p.originalWidth, p.originalHeight)
    : p.width && p.height ? printInches(p.width, p.height) : null

  return (
    <Dialog open title={p.caption ?? t.photo} onClose={onClose} className="roomviewer">
      <div className="roomviewer__layout">
        <div className="roomviewer__stage">
          {url ? (
            <img className="roomviewer__img" src={url} alt={description ?? p.caption ?? ''} />
          ) : (
            <span className="roomviewer__img roomviewer__img--wait" aria-busy="true" />
          )}
          <div className="roomviewer__nav">
            <button type="button" className="btn btn--quiet btn--sm" disabled={index === 0} onClick={() => onIndex(index - 1)}>
              ‹ <span className="sr-only">{t.previous}</span>
            </button>
            <span className="roomviewer__pos">{index + 1} / {photos.length}</span>
            <button type="button" className="btn btn--quiet btn--sm" disabled={index >= photos.length - 1} onClick={() => onIndex(index + 1)}>
              <span className="sr-only">{t.next}</span> ›
            </button>
            {p.originalWidth && (
              <button type="button" className="btn btn--link" onClick={() => setFull((f) => !f)} aria-pressed={full}>
                {full ? t.showingFullSize : t.showFullSize}
              </button>
            )}
          </div>
          {/* 148. A download says what it is for; a use the poster opted
              out of is not offered (and the server refuses it). */}
          <fieldset className="roomviewer__use">
            <legend>{t.useFor}</legend>
            {PHOTO_USES.map((k) => (
              <label key={k} className={uses && !uses[k] ? 'roomviewer__useopt roomviewer__useopt--off' : 'roomviewer__useopt'}>
                <input
                  type="radio"
                  name={`use-${p.path}`}
                  value={k}
                  checked={use === k}
                  disabled={!uses || !uses[k] || busy !== null}
                  onChange={() => setUse(k)}
                />
                {t.useLabel[k]}
              </label>
            ))}
          </fieldset>
          {/* 152. A review pauses every download; the photo stays up. */}
          {details?.underReview && <p className="roomviewer__review" role="status">{tr.reviewPaused}</p>}
          <div className="roomviewer__actions">
            {p.originalWidth && (
              <button type="button" className="btn btn--primary btn--sm" disabled={busy !== null || !use || details?.underReview === true} onClick={() => void take('original')}>
                {busy === 'dloriginal' ? t.downloading : t.downloadFull}
              </button>
            )}
            <button
              type="button"
              className={p.originalWidth ? 'btn btn--quiet btn--sm' : 'btn btn--primary btn--sm'}
              disabled={busy !== null || !use || details?.underReview === true}
              onClick={() => void take('feed')}
            >
              {busy === 'dlfeed' ? t.downloading : p.originalWidth ? t.downloadFeed : t.download}
            </button>
            {/* 304c: a Gallery upload's post page is where people are tagged (the
                uploader) and names suggested (anyone) — One never names people, so
                the door stays, called by what it is for. */}
            <Link className="btn btn--link" to={`/item/${p.contentId}?photo=${encodeURIComponent(p.path)}`}>
              {p.gallery ? t.tagPeople : t.openPost}
            </Link>
            {p.gallery && canLocation && !confirmRemove && (
              <button type="button" className="btn btn--link" disabled={busy !== null} onClick={() => setConfirmRemove(true)}>
                {t.removeFromGallery}
              </button>
            )}
          </div>
          {p.gallery && canLocation && confirmRemove && (
            <div className="roomviewer__row galadd__confirm" role="group" aria-label={t.removeFromGallery}>
              <span>{t.removeConfirm}</span>
              <button
                type="button"
                className="btn btn--danger btn--sm"
                disabled={busy !== null}
                onClick={() =>
                  void act('remove', async () => {
                    await removeGalleryPhoto(p.contentId)
                    setNotice({ text: t.removedNote, tone: 'warn' })
                    onWithdrawn(p.path)
                  })
                }
              >
                {t.removeYes}
              </button>
              <button type="button" className="btn btn--quiet btn--sm" onClick={() => setConfirmRemove(false)}>
                {t.removeNo}
              </button>
            </div>
          )}
          <PhotoReviewButton key={p.path} path={p.path} onChanged={() => setDetailsRev((n) => n + 1)} />
          {notice && (
            <p className={`photoroom__notice photoroom__notice--${notice.tone}`} role="status">
              {notice.text}
            </p>
          )}
        </div>

        <div className="roomviewer__panel">
          {score && (
            <p className="roomviewer__match">
              <strong>{t.match(score.score)}</strong>
              {score.why ? ` — ${score.why}` : ''}
            </p>
          )}

          <section className="roomviewer__section">
            <h3 className="roomviewer__h">{t.description}</h3>
            {editing ? (
              <div className="roomviewer__edit">
                <label>
                  {t.english}
                  <textarea rows={3} value={en} onChange={(e) => setEn(e.target.value)} maxLength={1200} />
                </label>
                <label>
                  {t.spanish}
                  <textarea rows={3} value={es} onChange={(e) => setEs(e.target.value)} maxLength={1200} />
                </label>
                <div className="roomviewer__row">
                  <button type="button" className="btn btn--primary btn--sm" disabled={busy !== null} onClick={() => void saveDescription()}>
                    {t.save}
                  </button>
                  <button type="button" className="btn btn--quiet btn--sm" onClick={() => setEditing(false)}>
                    {t.cancel}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className={description ? 'roomviewer__text' : 'roomviewer__text roomviewer__text--none'}>
                  {description ?? t.noDescription}
                </p>
                {p.descriptionBy && (
                  <p className="roomviewer__note">{p.descriptionBy === 'person' ? t.byPerson : t.byOneConetec}</p>
                )}
                <div className="roomviewer__row">
                  <button
                    type="button"
                    className="btn btn--link"
                    onClick={() => {
                      setEn(p.descriptionEn ?? '')
                      setEs(p.descriptionEs ?? '')
                      setEditing(true)
                    }}
                  >
                    {t.edit}
                  </button>
                  <button type="button" className="btn btn--link" disabled={busy !== null} onClick={() => void askAgain()}>
                    {busy === 'ask' ? t.asking : t.askAgain}
                  </button>
                </div>
              </>
            )}
          </section>

          <section className="roomviewer__section">
            <h3 className="roomviewer__h">{t.whatIsInIt}</h3>
            {FACETS.map((facet) => {
              const mine = p.features.filter((f) => f.facet === facet)
              const offer = vocab.filter((v) => v.facet === facet && !v.retired && v.tenantId === p.tenantId && !on.has(v.id))
              return (
                <div key={facet} className="roomviewer__facet">
                  <h4 className="roomviewer__fh">{t.facet[facet]}</h4>
                  <ul className="roomtags">
                    {mine.map((f) =>
                      f.state === 'accepted' ? (
                        <li key={f.valueId} className="roomtag">
                          {label(f)}
                          <button
                            type="button"
                            className="roomtag__x"
                            aria-label={`${label(f)}: ${t.notInPhoto}`}
                            title={t.notInPhoto}
                            disabled={busy !== null}
                            onClick={() => void no(f.valueId)}
                          >
                            ×
                          </button>
                        </li>
                      ) : (
                        <li key={f.valueId} className="roomtag roomtag--suggested" title={t.suggested(Math.round((f.confidence ?? 0.5) * 100))}>
                          {label(f)} <span className="roomtag__pct">{Math.round((f.confidence ?? 0.5) * 100)}%</span>
                          <button type="button" className="roomtag__yes" disabled={busy !== null} onClick={() => void yes(f.valueId)}
                            aria-label={`${label(f)}: ${t.yes}`}>
                            ✓
                          </button>
                          <button type="button" className="roomtag__x" disabled={busy !== null} onClick={() => void no(f.valueId)}
                            aria-label={`${label(f)}: ${t.no}`}>
                            ×
                          </button>
                        </li>
                      ),
                    )}
                    <li className="roomtag__pick">
                      {/* 321: search the tag list rather than scroll a dropdown (David, 29 Oct). */}
                      <SearchPick
                        className="roomtag__add"
                        options={offer.map((v) => ({ id: v.id, label: label(v) }))}
                        label={`${t.facet[facet]}: ${t.add}`}
                        placeholder={t.add}
                        disabled={busy !== null}
                        onPick={(o) => void yes(o.id)}
                        /* 152: the tag list itself is a Gallery admin's — typing a new one opens its form. */
                        onCreate={canLocation ? (text) => { setNewEn(text); setAdding(facet) } : undefined}
                      />
                    </li>
                  </ul>
                  {/* 305: what colleagues answered on this facet's tags, with names and reasons. */}
                  {crowd &&
                    crowd.features
                      .filter((f) => f.facet === facet && f.answers && f.answers.length > 0)
                      .map((f) => (
                        <ul key={f.valueId} className="crowd__answers" aria-label={`${label(f)}: ${tw.answers}`}>
                          {f.answers!.map((a, i) => (
                            <li key={i} className="crowd__answer">
                              <span className="crowd__who">{a.name}</span> · {a.yes ? tw.saidYes(label(f)) : tw.saidNo(label(f))}
                              {a.reason ? <span className="crowd__reason"> — “{a.reason}”</span> : null}
                            </li>
                          ))}
                        </ul>
                      ))}
                  {askNo && p.features.some((f) => f.valueId === askNo.valueId && f.facet === facet) && (
                    <TagReason
                      kind="no"
                      label={askNo.label}
                      confidence={askNo.confidence}
                      busy={busy !== null}
                      onSend={(reason) => void sendNo(askNo.valueId, reason)}
                      onCancel={() => setAskNo(null)}
                    />
                  )}
                  {adding === facet && (
                    <div className="roomviewer__row roomviewer__new">
                      <input value={newEn} onChange={(e) => setNewEn(e.target.value)} placeholder={t.newLabelEn} maxLength={60} />
                      <input value={newEs} onChange={(e) => setNewEs(e.target.value)} placeholder={t.newLabelEs} maxLength={60} />
                      <input value={newCue} onChange={(e) => setNewCue(e.target.value)} placeholder={tw.newCue} maxLength={280} />
                      <button type="button" className="btn btn--primary btn--sm" disabled={!newEn.trim() || busy !== null} onClick={() => void addNew(facet)}>
                        {t.save}
                      </button>
                      <button type="button" className="btn btn--quiet btn--sm" onClick={() => setAdding(null)}>
                        {t.cancel}
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </section>

          <section className="roomviewer__section">
            <h3 className="roomviewer__h">{t.people}</h3>
            <p className="roomviewer__text">
              {p.people.length ? `${t.withPeople} ${p.people.map((x) => x.name).join(', ')}` : t.nobodyTagged}
            </p>
            <p className="roomviewer__note">{t.consent(p.people.length)}</p>
            {uses && (
              <p className="roomviewer__note">
                {t.usesLine(
                  PHOTO_USES.filter((k) => uses[k]).map((k) => t.useLabel[k].toLowerCase()),
                  PHOTO_USES.filter((k) => !uses[k] && !uses.taggedNo).map((k) => t.useLabel[k].toLowerCase()),
                )}
                {uses.reason ? ` ${t.reasonLine(uses.reason)}` : ''}
              </p>
            )}
          </section>

          <section className="roomviewer__section">
            <h3 className="roomviewer__h">{t.details}</h3>
            <dl className="roomviewer__dl">
              {/* 304: a Gallery upload was added, not posted. */}
              <dt>{p.gallery ? t.addedBy : t.postedBy}</dt>
              <dd>
                {/* 300: under a byline, the byline first; the person is never hidden. */}
                {p.bylineName ? `${p.bylineName} — ` : ''}
                {[p.uploaderName, p.uploaderBu, p.uploaderCountry].filter(Boolean).join(' · ')}
              </dd>
              <dt>{p.gallery ? t.addedOn : t.posted}</dt>
              <dd>{dateOf(p.postedAt)}</dd>
              <dt>{t.taken}</dt>
              <dd>
                {/* 304: a typed date prints only as much as was given — "1998", never "1998-01-01 00:00". */}
                {p.takenAtLocal
                  ? p.gallery?.takenPrecision && p.gallery.takenPrecision !== 'minute'
                    ? takenText(p.takenAtLocal, p.gallery.takenPrecision, lang)
                    : takenOf(p.takenAtLocal)
                  : t.notRecorded}
                {p.camera ? ` · ${p.camera}` : ''}
              </dd>
              {p.gallery?.sourceName && canLocation && (
                <>
                  <dt>{t.fileName}</dt>
                  <dd>{p.gallery.sourceName}</dd>
                </>
              )}
              {/* 152: document users see the place NAME here, never the site; since 318 the point itself is on the Gallery map. */}
              {!canLocation && (
                <>
                  <dt>{tr.placeLabel}</dt>
                  <dd>{details === undefined ? '…' : placeName(details) ?? tr.placeNone}</dd>
                </>
              )}
              {canLocation && (
                <>
                  <dt>{t.where}</dt>
                  <dd>
                    {where === undefined ? '…' : where === null ? t.whereNotSent : (
                      <>
                        {placeLine(where) ?? t.wherePending}
                        <span className="roomviewer__coords"> · {t.coordinates(where.latitude, where.longitude)}</span>
                      </>
                    )}
                  </dd>
                </>
              )}
              {/* 321: the site, for document users and admins alike (David, 29 Oct). */}
              <PhotoSiteRow path={p.path} tenantId={p.tenantId} />
              <dt>{t.fullSize}</dt>
              <dd>
                {p.originalWidth && p.originalHeight
                  ? `${p.originalWidth.toLocaleString()} × ${p.originalHeight.toLocaleString()} px · ${megabytes(p.originalBytes) ?? ''}`
                  : t.noFullSize}
              </dd>
              <dt>{t.feedCopy}</dt>
              <dd>
                {p.width && p.height ? `${p.width.toLocaleString()} × ${p.height.toLocaleString()} px` : '—'}
                {p.byteSize ? ` · ${megabytes(p.byteSize)}` : ''}
              </dd>
              {print && (
                <>
                  <dt>{orient ? t.orientation[orient] : ''}</dt>
                  <dd>{t.printsAt(print.w, print.h)}</dd>
                </>
              )}
              {p.topics.length > 0 && (
                <>
                  <dt>{t.hashtags}</dt>
                  <dd>{p.topics.map((x) => '#' + x).join(' ')}</dd>
                </>
              )}
              {p.wins.length > 0 && (
                <>
                  <dt>{t.wins}</dt>
                  <dd>{p.wins.map((w) => `${t.place(w.place)} — ${w.title}`).join('; ')}</dd>
                </>
              )}
            </dl>
            {/* 319: add or move where it was taken (photo_place_state decides).
                322: and a small map of the point, from the same answer. */}
            <PlaceOnMap
              key={p.path}
              path={p.path}
              showMap
              className="placeonmap roomviewer__placeonmap"
              onPlaced={(path) => {
                setDetailsRev((n) => n + 1)
                onPlaced?.(path)
              }}
            />
          </section>

          {canLocation && (
            <section className="roomviewer__section">
              <h3 className="roomviewer__h">{tr.reviewsTitle}</h3>
              {reviews === null ? (
                <p className="roomviewer__text">…</p>
              ) : reviews.length === 0 ? (
                <p className="roomviewer__text roomviewer__text--none">{tr.reviewsNone}</p>
              ) : (
                <ul className="roomviewer__list">
                  {reviews.map((r) => (
                    <li key={r.id}>
                      <Link to={`/photos/reviews?open=${r.id}`}>
                        {r.reasons.map((x) => tr.reasonShort[x]).join(', ')}
                      </Link>
                      {r.requesterName ? ` · ${r.requesterName}` : ''} · {dateOf(r.requestedAt)}
                      {r.outcome ? ` · ${tr.outcomePast[r.outcome]}` : r.withdrawnAt ? ` · ${tr.withdrawnLine}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <section className="roomviewer__section">
            <h3 className="roomviewer__h">{t.downloads}</h3>
            <p className="roomviewer__text">
              {p.downloadCount === 0 ? t.neverDownloaded : t.downloadedTimes(p.downloadCount)}
              {p.starsPaid ? ` · ${t.starsPaid(reward)}` : ''}
            </p>
            {p.downloads.length > 0 && (
              <ul className="roomviewer__list">
                {p.downloads.map((d, i) => (
                  <li key={`${d.personId}-${d.at}-${i}`}>
                    {d.name} · {dateOf(d.at)}
                    {d.variant === 'original' ? ` · ${t.fullTag}` : ''}
                    {d.use ? ` · ${t.useLabel[d.use].toLowerCase()}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </Dialog>
  )
}

export function outcomeText(t: (typeof PHOTO_ROOM)['en'], outcome: DownloadOutcome, name: string, stars: number): string {
  switch (outcome) {
    case 'awarded':
      return t.outcome.awarded(name, stars)
    case 'duplicate':
      return t.outcome.duplicate
    case 'own_photo':
      return t.outcome.own_photo
    case 'byline':
      return t.outcome.byline
    case 'capped':
      return t.outcome.capped(name)
    case 'rule_off':
      return t.outcome.rule_off
    case 'not_member':
      return t.outcome.not_member(name)
    default:
      return t.outcome.failed
  }
}
